<template>
  <section
    class="hero-module-module-component"
    :class="data.home_or_page ? 'home-hero' : 'page-hero'">
    <video
      v-if="data.image_or_video"
      :id="`interactive-video-${imageId}`"
      ref="video"
      class="bg-video"
      playsinline
      muted>
      <source
        :src="data.bg_video.url"
        type="video/mp4" />
    </video>
    <img
      v-if="!data.image_or_video"
      :id="`interactive-img-${imageId}`"
      class="interactive-img"
      :style="{
        objectPosition: calculatePosition(),
      }"
      :src="
        data.image_or_video
          ? data.thumbnail.url
          : device === 'mobile' && data.bg_mobile
            ? data.bg_mobile.url
            : data.background_image.url
      "
      :alt="data.background_image.alt" />
    <div
      :id="`webgl-${interactiveContainer}`"
      class="interactive-section" />
    <div
      v-if="data.has_color_overlay"
      class="overlay"></div>
    <div
      v-if="data.has_color_overlay"
      class="top-overlay"></div>
    <div
      class="inner-container"
      :class="`${data.title_position ? 'center' : 'bottom'}`">
      <div
        class="line-div"
        :style="`${data.title_position ? `background: linear-gradient(180deg, ${data.line_color_start} 20%,  ${data.line_color_end} 100%);` : `background: linear-gradient(180deg, ${data.line_color_start} 60%,  ${data.line_color_end} 100%);`}`">
        <div
          v-if="data.orb"
          class="orb"
          :class="orb1"
          :style="`background-color: ${data.orb_color}; box-shadow: 0 0 20px ${data.orb_color};`"></div>
      </div>
      <div
        class="content-area"
        :class="data.show_button ? 'with-cta' : ''">
        <h1 class="hero-title">
          <span
            v-if="data.home_or_page"
            class="line one"
            >{{ data.title_line_one }}</span
          >
          <span
            v-if="data.home_or_page"
            class="line two"
            >{{ data.title_line_two }}</span
          >
          <span
            v-else
            class="line"
            v-html="data.title" />
        </h1>
        <ButtonGlobal
          v-if="data.show_button"
          class="hero-cta-button"
          :text="data.cta_button.title"
          :url="data.cta_button.url"
          :target="data.cta_button.target" />
      </div>
      <img
        v-if="data.show_arrow"
        class="arrow-scroll"
        src="@/assets/img/arrow-down.svg"
        alt="scroll-down" />
    </div>
  </section>
</template>

<script setup>
/* #META
   {
    "NAME": "HeroModuleComponent",
    "CLASSNAME": "hero-module-module-component",
    "URL": "http://lifemine.local/templates/"
   }
  #ENDMETA */
import gsap from 'gsap'
import useIntroShown from '@/composables/useIntroShown'
import { onMounted, onBeforeUnmount, ref } from 'vue'
import ButtonGlobal from '@/components/utility/ButtonGlobal.vue'
import { init } from '@/assets/js/scene.js'
import useUtils from '@/composables/useUtils'
import ScrollTrigger from 'gsap/ScrollTrigger'
import useReady from '@/composables/useReady'

gsap.registerPlugin(ScrollTrigger)

const { genUUID, device } = useUtils()
const { setReady, ready } = useReady()

const { introShown } = useIntroShown()
const video = ref(null)
const imageId = genUUID()
const interactiveContainer = genUUID()
const heroScrollTrigger1 = `hero-title-${genUUID()}`
const heroScrollTrigger2 = `hero-title-${genUUID()}`
const heroScrollTrigger3 = `hero-title-${genUUID()}`
const orb1 = 'orb1'
const renderer = ref(null)

const props = defineProps(['data'])

const webGL = async () => {
  setReady(false)
  renderer.value = await init(
    !props.data.image_or_video
      ? `#interactive-img-${imageId}`
      : `#interactive-video-${imageId}`,
    `#webgl-${interactiveContainer}`
  )
  setReady(true)
}

const calculatePosition = () => {
  if (device.value === 'desktop') {
    return `${props.data.image_position.horizontal_position} ${props.data.image_position.vertical_position}`
  } else {
    return `${props.data.image_position_mobile.horizontal_position_mobile} ${props.data.image_position_mobile.vertical_position_mobile}`
  }
}

const heroAnimation = () => {
  if (introShown.value && ready.value) {
    setTimeout(() => {
      gsap.fromTo(
        '.hero-module-module-component .hero-title .line',
        {
          y: 20,
          opacity: 0,
          ease: 'power4.inOut',
        },
        {
          duration: 2,
          y: 0,
          opacity: 1,
          stagger: 0.3,
          delay: !props.data.image_or_video ? 0 : 2,
          ease: 'power4.inOut',
        }
      )

      if (props.data.home_or_page) {
        gsap.fromTo(
          '.hero-module-module-component .hero-title .line.one',
          {
            x: 0,
          },
          {
            scrollTrigger: {
              trigger: '.hero-module-module-component .hero-title .line.one',
              id: heroScrollTrigger1,
              start:
                device.value === 'mobile' ? 'top center-=20%' : 'top center',
              end: 'bottom+=300% center+=10%',
              markers: false,
              scrub: 1,
            },
            x: -20,
          }
        )

        gsap.fromTo(
          '.hero-module-module-component .hero-title .line.two',
          {
            x: 0,
          },
          {
            scrollTrigger: {
              trigger: '.hero-module-module-component .hero-title .line.two',
              id: heroScrollTrigger2,
              start:
                device.value === 'mobile'
                  ? 'top-=150 center-=20%'
                  : 'top-=150 center',
              end: 'bottom+=350% center',
              markers: false,
              scrub: 1,
            },
            x: 100,
          }
        )
      }

      if (props.data.show_button) {
        gsap.fromTo(
          '.hero-module-module-component .hero-cta-button',
          {
            y: 20,
            opacity: 0,
            ease: 'power4.inOut',
          },
          {
            y: 0,
            duration: 2,
            opacity: 1,
            delay: 0.5,
            ease: 'power4.inOut',
          }
        )
      }

      if (device.value === 'desktop' && props.data.show_arrow) {
        gsap.fromTo(
          '.hero-module-module-component .arrow-scroll',
          {
            y: -100,
            scale: 1.3,
          },
          {
            scrollTrigger: {
              trigger: '.hero-module-module-component .arrow-scroll',
              id: heroScrollTrigger3,
              start: 'top-=300 center',
              end: 'bottom+=450% center',
              markers: false,
              scrub: 1,
            },
            y: 50,
            scale: 0.7,
          }
        )
      }
    }, 800)
  } else {
    setTimeout(() => {
      heroAnimation()
    }, 100)
  }
}

const defaultState = () => {
  if (!props.data.show_arrow) return
  gsap.set('.hero-module-module-component .arrow-scroll', {
    y: -100,
    scale: 1.3,
  })
}

const playVideo = () => {
  if (!introShown.value) {
    setTimeout(() => {
      playVideo()
    }, 100)
  } else {
    setTimeout(() => {
      if (video.value) {
        if (props.data.custom_loop) {
          video.value.play()
          video.value.addEventListener(
            'ended',
            function () {
              video.value.addEventListener(
                'timeupdate',
                function () {
                  if (this.currentTime >= props.data.loop_end) {
                    this.currentTime = props.data.loop_start
                    this.play()
                  }
                },
                false
              )
              this.currentTime = props.data.loop_start
              this.play()
            },
            false
          )
        } else {
          video.value.play()
        }
      }
    }, 500)
  }
}

onMounted(() => {
  defaultState()
  if (props.data.home_or_page) {
    webGL()
  } else {
    setReady(true)
  }
  heroAnimation()
  playVideo()
})

onBeforeUnmount(() => {
  if (props.data.home_or_page) {
    renderer.value.dispose()
  }
  ScrollTrigger.getById(heroScrollTrigger1)?.kill()
  ScrollTrigger.getById(heroScrollTrigger2)?.kill()
  ScrollTrigger.getById(heroScrollTrigger3)?.kill()
})
</script>

<style lang="scss">
.hero-module-module-component {
  @apply h-fit flex relative pb-10 lg:pb-0;
  &.home-hero {
    .bg-video,
    .overlay,
    .interactive-img,
    .interactive-section,
    .inner-container {
      @apply h-[95vh];
    }
  }
  &.page-hero {
    .bg-video,
    .overlay,
    .interactive-img,
    .interactive-section,
    .inner-container {
      @apply h-[80vh];
      h1.hero-title {
        @apply ml-6 lg:ml-8 w-[91%] lg:w-[78%];
      }
    }
  }
  .bg-video {
    @apply absolute inset-0 w-full object-cover object-bottom z-1;
  }
  .overlay {
    @apply absolute inset-0 bg-gradient-to-b from-lm_lavender to-lm_purple opacity-25 mix-blend-hard-light z-2;
  }
  .interactive-img {
    @apply absolute inset-0 w-full object-cover object-bottom z-1;
  }
  .interactive-section {
    @apply absolute inset-0 w-full object-cover object-bottom z-2 overflow-hidden;
    canvas {
      width: 100% !important;
      height: 100% !important;
    }
  }
  .top-overlay {
    @apply absolute inset-0 h-[300px] bg-gradient-to-b from-lm_purple to-lm_purple/0 opacity-50 mix-blend-multiply;
  }
  .inner-container {
    @apply relative flex items-end;
    &.center {
      @apply items-center;
      .content-area {
        @apply mb-0 mt-24 ml-8 lg:ml-6;
      }
    }
    .line-div {
      @apply w-[1px] h-[calc(100%-39px)] lg:h-[calc(100%-29px)] absolute left-[30px] lg:left-[60px] xl:left-[75px] top-[80px] lg:top-[110px] z-1;
      .orb {
        @apply absolute w-6 lg:w-8 h-6 lg:h-8 rounded-full top-[calc(50%-26px)] lg:top-[calc(50%-64px)] left-[calc(50%-12px)] lg:left-[calc(50%-16px)] blur-[2px];
      }
    }
    .content-area {
      @apply mb-16 lg:mb-28 flex flex-col;
      &.with-cta {
        @apply mb-16 lg:mb-16;
      }
      h1.hero-title {
        @apply heading-1 text-white flex flex-col ml-2 lg:ml-8 w-full;
        word-break: break-word;
        span {
          @apply text-[65px] xs:text-[78px] md:text-[87px] lg:text-[128px] heading-1 text-white;
        }
        .line {
          @apply opacity-0 mb-2;
          &.one {
            @apply max-w-[90%] md:max-w-full;
          }
          * {
            @apply text-[38px] xs:text-[44px] leading-[48px] lg:text-[56px] lg:leading-[59px] heading-2 text-white;
          }
          &.two {
            @apply ml-0 lg:ml-16 text-left;
          }
        }
      }
      .button-container {
        @apply opacity-0 ml-2 lg:ml-8;
      }
    }

    .arrow-scroll {
      @apply absolute w-[50px] h-[50px] lg:w-[72px] lg:h-[72px] -bottom-28 lg:-bottom-9 left-1/2 lg:left-[60px] xl:left-[75px] transform -translate-x-1/2 z-2;
    }
  }
}
</style>
